import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster, toast } from "sonner";
const _err = toast.error;
toast.error = (m, o = {}) => _err(m, { duration: 15000, closeButton: true, ...o });
import { AuthProvider, useAuth } from "@/context/AuthContext";
import AuthCallback from "@/pages/AuthCallback";
import GoogleCallback from "@/pages/GoogleCallback";
import CalendarCallback from '@/pages/CalendarCallback';
import Messages from "@/pages/Messages";
import Login from "@/pages/Login";
import Onboarding from "@/pages/Onboarding";
import Consent from "@/pages/Consent";
import Layout from "@/components/Layout";
import CommandCenter from "@/pages/CommandCenter";
import AthleteData from "@/pages/AthleteData";
import TaskDashboards from "@/pages/TaskDashboards";
import PlanBuilder from "@/pages/PlanBuilder";
import AddAthlete from "@/pages/AddAthlete";
import Correlations from "@/pages/Correlations";
import Performance from "@/pages/Performance";
import Strategy from "@/pages/Strategy";
import TaskLibrary from "@/pages/TaskLibrary";
import AthleteWorkspace from "@/pages/AthleteWorkspace";
import SquadBudget from "@/pages/SquadBudget";
import Compliance from "@/pages/Compliance";
import Billing from "@/pages/Billing";
import Settings from "@/pages/Settings";
import Catalog from "@/pages/Catalog";
import InviteAccept from "@/pages/InviteAccept";
import WearableConnected from "@/pages/WearableConnected";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/legal/Terms";
import Cookies from "@/pages/legal/Cookies";
import Refund from "@/pages/legal/Refund";
import Disclaimer from "@/pages/legal/Disclaimer";
import ResetPassword from "@/pages/ResetPassword";
import JoinNow from "@/pages/JoinNow";
import MyData from "@/pages/MyData";
import Fixtures from "@/pages/Fixtures";
import SquadCalendar from "@/pages/SquadCalendar";
import Digest from "@/pages/Digest";
import SquadWorkspace from "@/pages/SquadWorkspace";
import NotFound from "@/pages/NotFound";
import AdminDashboard from "@/pages/OperationsHub";
import JoinAcademy from "@/pages/EmailJoin";
import CoachDesk from "@/pages/CoachDesk";
import AthletePayments from "@/pages/AthletePayments";
import MySchedule from "@/pages/MySchedule";
import SharedTripBrief from "@/pages/SharedTripBrief";
import { ProtocolStudio } from "@/components/coaching/ProtocolStudio";
import WeeklyReports from "@/pages/WeeklyReports";
import { roleHome, joinDestination } from "@/lib/authDestination";

function Protected({ children, staffOnly = false, adminOnly = false }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <div className="min-h-screen flex items-center justify-center bg-background"><span className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground animate-pulse">Loading</span></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.role) return <Navigate to={joinDestination() || "/onboarding"} replace />;
  if (user.role === "athlete" && !user.consent_accepted) return <Navigate to="/consent" replace />;
  if ((staffOnly && user.role === "athlete") || (adminOnly && user.role !== "admin")) return <Navigate to={roleHome(user)} replace />;
  if (user.role === "athlete" && ["/academy", "/add-athlete", "/digest", "/tasks", "/library", "/correlations"].includes(location.pathname)) return <Navigate to="/athlete" replace />;
  return <Layout>{children}</Layout>;
}

// Command Center IS the squad workspace · the horizontal tabs (incl. the squad simulator) live here.
function HomeRoute() {
  const { user } = useAuth();
  if (user?.role === "coach" && !window.location.search) return <Navigate to="/coach" replace />;
  return user?.role === "athlete" ? <Navigate to="/athlete" replace /> : user?.role === "admin" && !window.location.search ? <Navigate to="/admin" replace /> : <SquadWorkspace />;
}

// Old standalone squad-workspace URL · everything moved into Command Center.
function SquadWorkspaceRedirect() {
  return <Navigate to={`/${useLocation().search}`} replace />;
}

// The squad simulator lives inside Command Center's tabs · a squad link here just goes there.
function SimulatorRoute() {
  const squad = new URLSearchParams(useLocation().search).get("squad");
  if (squad !== null) return <Navigate to={`/command-center?tab=simulator&squad=${encodeURIComponent(squad)}`} replace />;
  return <Navigate to="/athlete?tab=plan" replace />;
}

function WorkspaceRedirect({ tab }) {
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  query.set("tab", tab);
  return <Navigate to={`/athlete?${query}`} replace />;
}

function StudioRoute() {
  const query = new URLSearchParams(useLocation().search);
  return <Navigate to={`/athlete${query.get('athlete') ? '/'+encodeURIComponent(query.get('athlete')) : ''}?tab=plan&date=${encodeURIComponent(query.get('date') || '')}`} replace/>;
}

function AppRouter() {  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/auth/google" element={<GoogleCallback />} />
      <Route path="/onboarding" element={<Onboarding />} />
      <Route path="/consent" element={<Consent />} />
      <Route path="/invite/:token" element={<InviteAccept />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/cookies" element={<Cookies />} />
      <Route path="/refund" element={<Refund />} />
      <Route path="/disclaimer" element={<Disclaimer />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/membership" element={<JoinNow />} />
      <Route path="/my-data" element={<Protected><MyData /></Protected>} />
      <Route path="/academy" element={<Navigate to="/admin?tab=people" replace />} />
      <Route path="/join" element={<JoinAcademy />} />
      <Route path="/admin" element={<Protected adminOnly><AdminDashboard /></Protected>} />
      <Route path="/messages" element={<Protected><Messages /></Protected>} />
          <Route path="/coach/session" element={<Protected><StudioRoute /></Protected>} />
      <Route path="/weekly-reports" element={<Protected><WeeklyReports /></Protected>} />
      <Route path="/coach" element={<Protected staffOnly><CoachDesk /></Protected>} />
      <Route path="/trip-brief" element={<SharedTripBrief />} />
      <Route path="/my-schedule" element={<Protected><MySchedule /></Protected>} />
      <Route path="/calendar/google/callback" element={<Protected><CalendarCallback /></Protected>} />
      <Route path="/payments" element={<Protected><AthletePayments /></Protected>} />
      <Route path="/operations" element={<Protected staffOnly><AdminDashboard /></Protected>} />
      <Route path="/command-center" element={<Protected staffOnly><SquadWorkspace /></Protected>} />
      <Route path="/catalog" element={<Navigate to="/" replace />} />
      <Route path="/" element={<Protected><HomeRoute /></Protected>} />
      <Route path="/athlete" element={<Protected><AthleteWorkspace /></Protected>} />
      <Route path="/athlete/:id" element={<Protected><AthleteWorkspace /></Protected>} />
      <Route path="/squad-budget" element={<Protected staffOnly><SquadBudget /></Protected>} />
      <Route path="/athletes/:id" element={<Protected><AthleteData /></Protected>} />
      <Route path="/tasks" element={<Protected><TaskDashboards /></Protected>} />
      <Route path="/plan" element={<WorkspaceRedirect tab="plan" />} />
      <Route path="/simulator" element={<Protected staffOnly><SimulatorRoute /></Protected>} />
      <Route path="/fixtures" element={<Navigate to="/?tab=fixtures" replace />} />
      <Route path="/squad-calendar" element={<Navigate to="/?tab=calendar" replace />} />
      <Route path="/digest" element={<Protected><Digest /></Protected>} />
      <Route path="/squad-workspace" element={<Protected><SquadWorkspaceRedirect /></Protected>} />
      <Route path="/squad" element={<Navigate to="/" replace />} />
      <Route path="/performance" element={<WorkspaceRedirect tab="training" />} />
      <Route path="/training" element={<WorkspaceRedirect tab="training" />} />
      <Route path="/strategy" element={<WorkspaceRedirect tab="strategy" />} />
      <Route path="/library" element={<Protected><TaskLibrary /></Protected>} />
      <Route path="/import" element={<Navigate to="/" replace />} />
      <Route path="/correlations" element={<Protected><Correlations /></Protected>} />
      <Route path="/compliance" element={<Navigate to="/settings/compliance" replace />} />
      <Route path="/billing" element={<Navigate to="/settings/billing" replace />} />
      <Route path="/settings/*" element={<Protected><Settings /></Protected>} />
      <Route path="/wearables/connected" element={<Protected><WearableConnected /></Protected>} />
      <Route path="*" element={<Protected><NotFound /></Protected>} />
      <Route path="/add-athlete" element={<Protected><AddAthlete /></Protected>} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRouter />
        <Toaster position="bottom-right" theme="light" toastOptions={{ duration: 6000 }} />
      </BrowserRouter>
    </AuthProvider>
  );
}
